// Assisted-reconcile list. Opened from the "Show probable matches" button in
// the workspace when invoices/SAP rows are left over after auto-match (e.g. an
// invoice arrives after its SAP row already posted). Shows ranked 1-to-1
// candidate pairs (≥45%, highest first) with the same match chips as the
// workspace, and a Reconcile button on each for a one-click confirm.

import React, { useState } from 'react';
import { TOKENS, UI_FONT, CODE_STYLE } from './tokens';
import { Modal, Mono, Tone, MoneyCell, Icon, styles } from './primitives';

const fmtDate = (d) => (d ? String(d).slice(0, 10) : '—');

export default function ProbableMatchesModal({ matches, onReconcile, onClose }) {
  const [busyId, setBusyId] = useState(null);
  // Locally hide a pair the instant its reconcile succeeds. The parent also
  // refreshes the workspace (which recomputes `matches`), so this is just to
  // avoid a flash of the row before that round-trip lands.
  const [done, setDone] = useState(() => new Set());

  const visible = matches.filter((m) => !done.has(m.sap.id));

  async function reconcile(m) {
    setBusyId(m.sap.id);
    const ok = await onReconcile([m.sap.id], [m.invoice.id]);
    setBusyId(null);
    if (ok) setDone((prev) => new Set(prev).add(m.sap.id));
    // On failure onReconcile surfaces a toast (e.g. sum mismatch); leave the
    // row in place so the user can try a manual pairing instead.
  }

  return (
    <Modal
      title="Probable matches"
      subtitle="Ranked by confidence · click Reconcile to confirm a pair"
      onClose={onClose}
      wide
    >
      {visible.length === 0 ? (
        <div
          style={{
            padding: '28px 0',
            textAlign: 'center',
            color: TOKENS.ink3,
            fontFamily: UI_FONT,
            fontSize: 13,
          }}
        >
          {matches.length === 0
            ? 'No probable matches at or above 45%.'
            : 'All caught up — every suggested pair has been reconciled.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visible.map((m) => (
            <PairCard
              key={`${m.sap.id}|${m.invoice.id}`}
              m={m}
              busy={busyId === m.sap.id}
              onReconcile={() => reconcile(m)}
            />
          ))}
        </div>
      )}
    </Modal>
  );
}

function PairCard({ m, busy, onReconcile }) {
  const pct = Math.round(m.score * 100);
  const strong = m.score >= 0.7;
  const sapVendor =
    m.sap.inferred_vendor && m.sap.inferred_vendor !== '—'
      ? m.sap.inferred_vendor
      : 'unknown vendor';
  return (
    <div style={{ border: `1px solid ${TOKENS.border}`, borderRadius: 9, overflow: 'hidden' }}>
      <Side
        label="SAP"
        title={sapVendor}
        sub={`REF ${m.sap.reference_field || '—'} · ${fmtDate(m.sap.posting_date)}`}
        amount={m.sap.amount}
        currency={m.sap.currency}
        badge={
          <Tone tone={strong ? 'good' : 'soft'} icon={<Icon d="M5 12l4 4 10-10" size={9} sw={3} />}>
            {pct}%
          </Tone>
        }
      />
      <div style={{ height: 1, background: TOKENS.border }} />
      <Side
        label="INV"
        title={m.invoice.vendor || 'unknown vendor'}
        sub={`${m.invoice.invoice_number || '—'} · ${fmtDate(m.invoice.invoice_date)}`}
        amount={m.invoice.amount}
        currency={m.invoice.currency}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexWrap: 'wrap',
          padding: '8px 12px',
          background: TOKENS.panelAlt,
          borderTop: `1px solid ${TOKENS.border}`,
        }}
      >
        {m.signals.map((s, i) => (
          <Tone key={i} tone={s.tone} title={s.tip}>
            {s.label}
          </Tone>
        ))}
        <button
          onClick={onReconcile}
          disabled={busy}
          style={{ marginLeft: 'auto', ...styles.primaryBtn, opacity: busy ? 0.6 : 1 }}
        >
          {busy ? (
            'Reconciling…'
          ) : (
            <>
              <Icon d="M5 12l4 4 10-10" size={12} sw={2.5} />
              Reconcile
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function Side({ label, title, sub, amount, currency, badge }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
      <Mono size={9} color={TOKENS.ink3} style={{ width: 26, flex: '0 0 auto' }}>
        {label}
      </Mono>
      <div style={{ minWidth: 0, flex: '1 1 auto' }}>
        <div
          style={{
            fontFamily: UI_FONT,
            fontSize: 12.5,
            fontWeight: 550,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </div>
        {/* SAP doc reference / invoice number — identifiers get the slashed zero. */}
        <div style={{ ...CODE_STYLE, fontSize: 10.5, color: TOKENS.ink3 }}>{sub}</div>
      </div>
      {badge}
      <MoneyCell value={Number(amount)} currency={currency || 'EUR'} size={13} />
    </div>
  );
}
